import {
    AreaSeries,
    CandlestickSeries,
    ColorType,
    createChart,
    HistogramSeries,
    type IChartApi,
    type IPriceLine,
    type ISeriesApi,
    LineSeries,
} from 'lightweight-charts'
import { Activity } from 'lucide-react'
import { useEffect, useRef } from 'react'
import { CHART_COLORS, CHART_THEME, CHART_THEME_DARK } from '../../config/charts'
import { formatISTTime } from '../../lib/date-utils'
import { useSettingsStore } from '../../stores/settingsStore'
import { ChartContainer } from './ChartContainer'

// ... imports

interface ChartProps {
    data: any[]
    type?: 'Area' | 'Candlestick' | 'Histogram' | 'Line'
    height?: number
    colors?: {
        backgroundColor?: string
        textColor?: string
        lineColor?: string
        areaTopColor?: string
        areaBottomColor?: string
    }
    title?: string
    subtitle?: string
    tickLineValue?: number // NEW
}

export const FinancialChart: React.FC<ChartProps> = ({
    data,
    type = 'Area',
    height = 300,
    colors = {},
    title = 'Market Trend',
    subtitle,
    tickLineValue, // NEW
}) => {
    const chartContainerRef = useRef<HTMLDivElement>(null)
    const chartRef = useRef<IChartApi | null>(null)
    const seriesRef = useRef<ISeriesApi<any> | null>(null)
    const tickLineRef = useRef<IPriceLine | null>(null) // NEW
    const resizeObserver = useRef<ResizeObserver | null>(null)
    const theme = useSettingsStore((s) => s.theme)
    const currentTheme = theme === 'dark' ? CHART_THEME_DARK : CHART_THEME

    // 1. Initialize Chart (Once)
    useEffect(() => {
        if (!chartContainerRef.current) return

        // Apply Theme Merging
        const chartOptions = {
            ...currentTheme,
            layout: {
                ...currentTheme.layout,
                background: {
                    type: ColorType.Solid,
                    color: colors.backgroundColor || 'transparent',
                },
                textColor: colors.textColor || currentTheme.layout.textColor,
            },
            width: chartContainerRef.current.clientWidth,
            height: height,
            localization: {
                timeFormatter: (time: number) => {
                    if (typeof time === 'number') {
                        return formatISTTime(time * 1000)
                    }
                    return String(time)
                },
            },
            timeScale: {
                ...currentTheme.timeScale,
                tickMarkFormatter: (time: number) => {
                    if (typeof time === 'number') {
                        return formatISTTime(time * 1000)
                    }
                    return String(time)
                },
            },
            handleScroll: {
                mouseWheel: true,
                pressedMouseMove: true,
                horzTouchDrag: true,
                vertTouchDrag: false,
            },
            handleScale: {
                mouseWheel: true,
                pinch: true,
                axisPressedMouseMove: true,
                axisDoubleClickReset: true,
            },
        }

        const chart = createChart(chartContainerRef.current, chartOptions as any)
        chartRef.current = chart

        // Create Series
        let newSeries: ISeriesApi<any>

        try {
            if (type === 'Candlestick') {
                newSeries = chart.addSeries(CandlestickSeries, {
                    upColor: CHART_COLORS.SUCCESS,
                    downColor: CHART_COLORS.DANGER,
                    borderVisible: false,
                    wickUpColor: CHART_COLORS.SUCCESS,
                    wickDownColor: CHART_COLORS.DANGER,
                })
            } else if (type === 'Histogram') {
                newSeries = chart.addSeries(HistogramSeries, {
                    color: colors.lineColor || CHART_COLORS.INFO,
                    priceFormat: {
                        type: 'volume',
                    },
                })
            } else {
                // Area or Line
                const seriesOptions = {
                    lineColor: colors.lineColor || CHART_COLORS.PRIMARY,
                    lineWidth: 2 as any,
                }

                if (type === 'Area') {
                    newSeries = chart.addSeries(AreaSeries, {
                        ...seriesOptions,
                        topColor: colors.areaTopColor || CHART_COLORS.AREA_TOP_DEFAULT,
                        bottomColor: colors.areaBottomColor || CHART_COLORS.AREA_BOTTOM_DEFAULT,
                    })
                } else {
                    newSeries = chart.addSeries(LineSeries, seriesOptions)
                }
            }
            seriesRef.current = newSeries
        } catch (e) {
            console.error('[FinancialChart] Failed to create series:', e)
        }

        // Robust Resize Handling using ResizeObserver
        let hasFitContent = false
        let resizeRaf: number | null = null
        let lastObservedWidth = Math.floor(chartContainerRef.current?.clientWidth || 0)
        resizeObserver.current = new ResizeObserver((entries) => {
            const entry = entries[0]
            if (!chartRef.current || !entry) return
            const newWidth = Math.floor(entry.contentRect.width)
            if (newWidth > 0 && Math.abs(newWidth - lastObservedWidth) >= 4) {
                lastObservedWidth = newWidth
                if (resizeRaf !== null) cancelAnimationFrame(resizeRaf)
                resizeRaf = requestAnimationFrame(() => {
                    if (chartRef.current) {
                        chartRef.current.applyOptions({ width: newWidth })
                        if (!hasFitContent) {
                            chartRef.current.timeScale().fitContent()
                            hasFitContent = true
                        }
                    }
                })
            }
        })

        resizeObserver.current.observe(chartContainerRef.current)

        return () => {
            if (resizeRaf !== null) cancelAnimationFrame(resizeRaf)
            if (resizeObserver.current) resizeObserver.current.disconnect()
            if (chartRef.current) {
                try {
                    chartRef.current.remove()
                } catch (err) {
                    console.warn('[FinancialChart] Error removing chart:', err)
                }
                chartRef.current = null
            }
            seriesRef.current = null
            tickLineRef.current = null
        }
    }, [
        type,
        height,
        colors.backgroundColor,
        colors.textColor,
        colors.areaBottomColor,
        colors.areaTopColor,
        colors.lineColor,
        theme, // Re-run effect on theme change
    ])

    // Theme Update Effect (Non-destructive)
    useEffect(() => {
        if (chartRef.current) {
            chartRef.current.applyOptions({
                layout: {
                    textColor: currentTheme.layout.textColor,
                    background: { type: ColorType.Solid, color: 'transparent' },
                },
                grid: currentTheme.grid,
                timeScale: currentTheme.timeScale,
                rightPriceScale: currentTheme.rightPriceScale,
                crosshair: currentTheme.crosshair as any,
            })
        }
    }, [theme])

    // 2. Update Data
    useEffect(() => {
        if (!seriesRef.current) return

        // SANITIZATION & OPTIMIZATION PIPELINE
        // 1. Filter Invalid Types
        let processedData = data
            .filter((d: any) => {
                if (!d || d.time === undefined || d.time === null) return false
                // Strict check: time must be a number (Unix timestamp) or a valid date string YYYY-MM-DD
                if (typeof d.time === 'number' && !Number.isNaN(d.time)) return true
                if (typeof d.time === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(d.time)) return true
                return false
            })
            .map((d: any) => {
                // Ensure specific color overrides if present (e.g. for histogram)
                return { ...d, time: d.time as any, color: d.color }
            })

        // 2. Filter Values (NaN/Null)
        processedData = processedData.filter((d: any) => {
            if (type === 'Candlestick') {
                return (
                    typeof d.open === 'number' &&
                    !Number.isNaN(d.open) &&
                    typeof d.high === 'number' &&
                    !Number.isNaN(d.high) &&
                    typeof d.low === 'number' &&
                    !Number.isNaN(d.low) &&
                    typeof d.close === 'number' &&
                    !Number.isNaN(d.close)
                )
            }
            // For Area/Line
            return d.value !== undefined && d.value !== null && !Number.isNaN(d.value) && Number.isFinite(d.value)
        })

        // 3. SORT & DEDUPLICATE (Critical for Lightweight Charts)
        // Sort by time ascending
        processedData.sort((a: any, b: any) => (a.time as number) - (b.time as number))

        // Deduplicate (Keep last for same timestamp)
        const uniqueData: any[] = []
        let lastTime: any = null
        if (processedData.length > 0) {
            lastTime = processedData[0].time
            uniqueData.push(processedData[0])

            for (let i = 1; i < processedData.length; i++) {
                const item = processedData[i]
                if (item.time !== lastTime) {
                    uniqueData.push(item)
                    lastTime = item.time
                } else {
                    // Replace last item with current (latest update wins)
                    uniqueData[uniqueData.length - 1] = item
                }
            }
        }

        // 4. LIMIT / DOWNSAMPLE
        const MAX_POINTS = 3000
        let finalData = uniqueData

        if (uniqueData.length > MAX_POINTS) {
            const step = Math.ceil(uniqueData.length / MAX_POINTS)
            finalData = uniqueData.filter((_, i) => i % step === 0)

            // Critical: Always include the very last data point (Latest Price)
            const lastOriginal = uniqueData[uniqueData.length - 1]
            const lastIncluded = finalData[finalData.length - 1]

            if (lastOriginal && lastIncluded && lastOriginal.time !== lastIncluded.time) {
                finalData.push(lastOriginal)
            }
        }

        try {
            if (seriesRef.current) {
                seriesRef.current.setData(finalData)
            }
            if (chartRef.current && finalData.length > 0) {
                chartRef.current.timeScale().fitContent()
            }
        } catch (e) {
            console.error('[FinancialChart] Data Set Error:', e)
        }
    }, [data, type])

    // 3. Handle Tick Line / Highlight Value
    useEffect(() => {
        if (!seriesRef.current) return

        // If we already have a tick line, remove it
        if (tickLineRef.current) {
            seriesRef.current.removePriceLine(tickLineRef.current)
            tickLineRef.current = null
        }

        if (tickLineValue !== undefined) {
            tickLineRef.current = seriesRef.current.createPriceLine({
                price: tickLineValue,
                color: '#6366f1',
                lineWidth: 2 as any,
                lineStyle: 3 as any, // Dashed
                axisLabelVisible: true,
                title: 'Selected',
                axisLabelColor: '#4f46e5',
                axisLabelTextColor: '#ffffff',
            })
        }
    }, [tickLineValue])

    return (
        <ChartContainer
            title={title}
            subtitle={subtitle || (type === 'Area' ? 'Price Action' : 'Trend Analysis')}
            icon={<Activity size={18} strokeWidth={2.5} />}
        >
            <div ref={chartContainerRef} className="w-full relative overflow-hidden" />
        </ChartContainer>
    )
}
